import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  onSnapshot,
  Timestamp,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
// SCHEDULE GLOBALIZATION (2026-08-01): bound guest booking slots to the owner's shop hours.
import { publicHoursService } from './publicHoursService';
// PER-SHOP BOOKING RULES (2026-08-07): back-to-back / buffer / interval / no-show policy the owner set.
import { publicBookingRulesService } from './publicBookingRulesService';
// SHARED AVAILABILITY BRAIN (2026-08-07): guest slots now unified onto bookableSlots — the SAME
// shop∩barber∩free + back-to-back logic the customer portal + POS use. DOUBLE-BOOK GUARD (2026-08-05).
import { bookableSlots, minutesToLabel, hasApptConflict, parseTimeToMinutes } from '../shared/availability';
import { durationForBooking } from '../shared/newClientAppointment';
import { DEFAULT_NO_SHOW_NOTICE } from '../shared/noShowBlock';
import { normalizePhone } from '../shared/identity';

export interface Barber {
  id: string;
  name: string;
  avatar?: string;
  specialties: string[];
  rating: number;
  bio?: string;
  isActive: boolean;
}

export interface Service {
  id: string;
  name: string;
  duration: number;
  price: number;
  category: string;
  description: string;
}

export interface Appointment {
  id?: string;
  clientId: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  barberId: string;
  barberName: string;
  service: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  duration: number;
  price: number;
  status: 'scheduled' | 'confirmed' | 'checked-in' | 'in-progress' | 'completed' | 'cancelled' | 'no-show';
  notes?: string;
  createdAt?: Date;
  source: 'online' | 'phone' | 'walk-in' | 'app' | 'import';
  reminderSent: boolean;
}

class BookingService {
  /**
   * Get all active barbers
   */
  async getBarbers(): Promise<Barber[]> {
    const q = query(collection(db, 'barbers'), where('isActive', '==', true));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Barber[];
  }

  /**
   * Get barber by ID
   */
  async getBarber(barberId: string): Promise<Barber | null> {
    const docRef = doc(db, 'barbers', barberId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data()
    } as Barber;
  }

  /**
   * Get all services
   */
  async getServices(): Promise<Service[]> {
    const snapshot = await getDocs(collection(db, 'services'));

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Service[];
  }

  /**
   * Get available time slots for a barber on a specific date
   */
  async getAvailableSlots(barberId: string, date: string, intervalOverrideMin?: number, serviceDurationMin?: number): Promise<string[]> {
    // UNIFIED ON THE SHARED BRAIN (2026-08-07): guest booking now computes availability the SAME way
    // as the customer portal + POS — shop OPEN ∩ barber WORKING ∩ slot FREE, back-to-back, per-shop
    // rules. Previously this had its OWN parallel slot logic that (a) ignored the barber's day-off
    // schedule (a guest could book a barber on their off day if the shop was open) and (b) hardcoded
    // DEFAULT rules. Both fixed by routing through bookableSlots. `date` is 'yyyy-mm-dd'.
    // 🔴 KILL SWITCH: owner paused online booking (publicConfig/bookingPause) → offer NO slots. Fresh read;
    // on any error, do NOT pause (never block legit booking by accident).
    const reqDate = new Date(date + 'T12:00:00');   // noon → avoid TZ day-shift
    // PERF (Jose 2026-11-02, "times spin forever after you pick a day"): kill-switch, shop hours, rules,
    // barber schedule, this-day occupancy, this-day blocks are all INDEPENDENT reads — they were chained
    // sequentially (6 round-trips → the long spin). Fire them together: one round-trip. Pause read and
    // blockedTimes both fail-open (never block a legit booking on a read error).
    const [pSnap, dayHours, rules, bSnap, snapshot, blkSnap] = await Promise.all([
      getDoc(doc(db, 'publicConfig', 'bookingPause')).catch(() => null),
      publicHoursService.getDayHours(reqDate),
      publicBookingRulesService.getRules(),
      getDoc(doc(db, 'barbers', barberId)),
      getDocs(query(collection(db, 'slotOccupancy'), where('barberId', '==', barberId), where('date', '==', date))),
      getDocs(query(collection(db, 'blockedTimes'), where('barberId', '==', barberId), where('date', '==', date)))
        .catch((e) => { console.warn('blockedTimes read failed (non-fatal):', e); return null; }),
    ]);
    // 🔴 KILL SWITCH: owner paused online booking → offer NO slots.
    if (pSnap && pSnap.exists() && (pSnap.data() as any)?.paused === true) return [];
    if (dayHours.closed) {
      console.log(`🚫 Shop closed on ${publicHoursService.getDayName(reqDate)} — no guest slots offered.`);
      return [];
    }

    // WALK-IN quarter-hour grid (Jose 2026-10-19): the Open-Chair walk-in page forces a 15-min
    // interval regardless of the shop's global booking interval, so "next quarter hour" slots exist
    // even in a 30-min shop. Only the candidate grid spacing changes — shop hours ∩ barber schedule ∩
    // free is untouched, so offered slots stay truthful.
    const effRules = (intervalOverrideMin && intervalOverrideMin > 0)
      ? { ...rules, slotIntervalMin: intervalOverrideMin }
      : rules;

    // The barber's real weekly schedule (read raw — getBarber() drops `availability`).
    const barber: any = bSnap.exists()
      ? { id: barberId, availability: (bSnap.data() as any).availability }
      : { id: barberId };

    // This barber's occupancy on this date — from the PII-free `slotOccupancy` projection (no client
    // names/prices), so the public guest app never reads the raw appointment book. Cancelled +
    // product-only rows are already excluded by the projection. (Security fix 2026-09-28)
    const appts = snapshot.docs
      .map(d => d.data())
      .filter(a => a.status !== 'cancelled')
      .map(a => ({ barberId, time: a.time, duration: a.duration || 30, status: a.status }));

    // ⛔ Barber time-blocks (lunch/errands, set in POS) occupy exactly like an appointment — merge the
    // shared `blockedTimes` collection so the public guest app can't book over a block. (2026-08-12)
    if (blkSnap) {
      blkSnap.docs.forEach(d => {
        const b: any = d.data();
        appts.push({ barberId, time: b.startTime, duration: b.duration || 30, status: 'confirmed' });
      });
    }

    // Shop hours ∩ barber schedule ∩ free — back-to-back — from the ONE shared brain.
    // CLIENT-FACING GRID (Jose 2026-10-20): clients must ONLY ever see clean grid times (:00/:30 at a
    // 30-min shop). back-to-back packing (bookableSlots) also offers the exact minute a barber frees up
    // after each appointment — great for staff squeezing, but a 45-min cut ending at 1:15 was surfacing
    // "1:15" to clients and hiding 1:00. Snap the client grid to the interval (anchored to the hour).
    // The walk-in Open-Chair page passes a 15-min override, so it still gets its :15 quarter-hour slots.
    const gridMin = effRules.slotIntervalMin > 0 ? effRules.slotIntervalMin : 30;
    // SERVICE-FIT DEFAULT (Jose 2026-10-23, "a 30-min service should never show a 15-min opening"): the
    // fit filter only ran when a caller passed a duration. Time-first surfaces (Open Chairs, rebook,
    // quick-pick, family) passed NONE → offered any free minute, incl. an 11:00 with 15 min before the
    // next appt. Default the fit to a standard 30-min cut when no POSITIVE duration is given, so no
    // client surface can ever offer a sub-30 opening. Surfaces that pass a real (shorter) duration win.
    const fitMin = (serviceDurationMin && serviceDurationMin > 0) ? serviceDurationMin : 30;
    // NO PAST SLOTS TODAY (Jose 2026-10-24): never offer a time earlier than NOW on the current local day.
    // Parity with the portal fix — a "today" picker must not surface already-passed slots as free.
    const _now = new Date();
    const _todayStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
    const _nowMin = date === _todayStr ? _now.getHours() * 60 + _now.getMinutes() : -1;
    const slots = bookableSlots(barber, reqDate, appts, {
      closed: dayHours.closed,
      open: dayHours.open,
      close: dayHours.close,
    }, effRules, undefined, fitMin)   // ← service-fit: only slots the full service fits (default 30)
      .filter(m => m % gridMin === 0)
      .filter(m => m > _nowMin)   // drop already-passed times when date === today
      .map(minutesToLabel);

    console.log('✅ Guest slots (shop∩barber∩free, back-to-back):', slots.length);
    return slots;
  }

  /**
   * Normalize time format to 12-hour format with AM/PM
   * Converts "10:00" or "10:00:00" to "10:00 AM"
   * Leaves "10:00 AM" as-is
   */
  private normalizeTimeFormat(time: string): string {
    // If already in 12-hour format (contains AM/PM), return as-is
    if (time.includes('AM') || time.includes('PM')) {
      return time;
    }

    // Parse 24-hour format (e.g., "10:00" or "14:30")
    const timeParts = time.split(':');
    let hour = parseInt(timeParts[0], 10);
    const minute = timeParts[1];

    // Determine AM/PM
    const period = hour < 12 ? 'AM' : 'PM';

    // Convert to 12-hour format
    if (hour === 0) {
      hour = 12; // Midnight
    } else if (hour > 12) {
      hour = hour - 12; // PM hours
    }

    return `${hour}:${minute} ${period}`;
  }

  /**
   * Reschedule (MOVE) an existing appointment to a new date/time, optionally onto a new barber — used by
   * the reschedule-offer claim (a departed barber's client picks a new slot). Same fresh-read double-book
   * guard as createAppointment, ignoring this appointment's own doc. (Jose 2026-09-26)
   */
  async rescheduleAppointment(appointmentId: string, newDate: string, newTime: string, newBarberId?: string, newBarberName?: string): Promise<void> {
    try {
      const apptSnap = await getDoc(doc(db, 'appointments', appointmentId));
      const cur: any = apptSnap.exists() ? apptSnap.data() : null;
      const reqStart = parseTimeToMinutes(newTime);
      const targetBarberId = newBarberId || cur?.barberId;
      if (targetBarberId && reqStart != null) {
        const existing = await getDocs(query(
          collection(db, 'slotOccupancy'), // PII-free occupancy for the reschedule guard (Security 2026-09-28)
          where('barberId', '==', targetBarberId),
          where('date', '==', newDate),
        ));
        const appts = existing.docs.map(d => ({
          id: d.id, barberId: d.data().barberId, time: d.data().time, duration: d.data().duration, status: d.data().status,
        }));
        const rules = await publicBookingRulesService.getRules();
        const conflict = hasApptConflict(targetBarberId, reqStart, cur?.duration || 30, appts, appointmentId, rules.noShowOccupiesSlot);
        if (conflict.conflict) {
          throw new Error(`That time was just taken (overlaps ${conflict.withTime}). Please pick another slot.`);
        }
      }
    } catch (e: any) {
      if (/just taken/.test(e?.message || '')) throw e;
      console.warn('Reschedule fresh-guard read failed (non-fatal, proceeding):', e);
    }
    await updateDoc(doc(db, 'appointments', appointmentId), {
      date: newDate,
      time: newTime,
      ...(newBarberId ? { barberId: newBarberId, barberName: newBarberName || '' } : {}),
      status: 'confirmed',
      rescheduledAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Create new appointment
   */
  async createAppointment(appointmentData: Omit<Appointment, 'id' | 'createdAt'>): Promise<Appointment> {
    // The DOUBLE-BOOK GUARD runs AFTER new-client detection (below) so it can use the EFFECTIVE
    // duration — a new client's 45-min minimum can overlap the NEXT appointment even when the base
    // slot looked free. Checking with the base duration let those overlaps through (then the server
    // referee killed them and they "disappeared"). reqStart is precomputed; the guard fires pre-write.
    const reqStart = parseTimeToMinutes(appointmentData.time);

    // NEW-CLIENT (2026-08-07): a first-timer booking online → 45-min min + flagged so the POS
    // calendar shows a "New Client" badge. "Been here before" = a client record with prior visits;
    // no record (or a record with 0 visits) = new. Phone is the reliable dedup key. Best-effort —
    // never block a booking on detection. Same logic as the POS (shared/newClientAppointment.ts).
    let isNew = false;
    let resolvedClientId: string | null = null;
    let createGuestProfile = false; // defer the guest-profile write until AFTER the conflict guard passes
    try {
      const digits = normalizePhone(appointmentData.clientPhone);
      const nm = (appointmentData.clientName || '').trim();
      const email = (appointmentData.clientEmail || '').trim().toLowerCase();
      const hasIdentity = digits.length >= 7 || (!!nm && !/^walk-in/i.test(nm) && nm.toLowerCase() !== 'guest');
      if (hasIdentity) {
        // Identity match SERVER-SIDE (Security 2026-09-28): the guest never reads the `clients` book
        // (client PII). The resolveClientForBooking Cloud Function does the phone→email→name match with
        // admin access + returns only the result. Same phone-first logic as before (shared/identity.ts).
        const resolveFn = httpsCallable(getFunctions(), 'resolveClientForBooking');
        const res: any = (await resolveFn({
          phone: appointmentData.clientPhone, email: appointmentData.clientEmail, name: appointmentData.clientName,
        })).data;
        if (!res?.clientId) {
          isNew = true;
          createGuestProfile = true; // real profile created AFTER the guard passes (never orphan on a blocked slot)
        } else {
          resolvedClientId = res.clientId;
          isNew = !!res.isNew;
          // NO-SHOW BLOCK enforcement (2026-08-07; real-charge 2026-10-25): a blocked client must prepay
          // to book online. A REAL Stripe charge (prepaidPaymentIntentId, via PrepaymentModal) OR the
          // legacy e-signature agreement unblocks. Without either, stop with the notice. shared/noShowBlock.ts
          if (res.noShowBlocked === true
              && !(appointmentData as any).prepaidPaymentIntentId
              && !(appointmentData as any).prepaidAgreement) {
            const err: any = new Error(res.noShowNotice || DEFAULT_NO_SHOW_NOTICE);
            err.code = 'NO_SHOW_BLOCK';
            throw err;
          }
        }
      }
    } catch (e: any) {
      if (e?.code === 'NO_SHOW_BLOCK') throw e; // the block must NOT be swallowed
      console.warn('New-client detection failed (non-fatal):', e);
    }

    // PER-SHOP new-client minimum (BookingRules.newClientMinDuration) — mirrors POS. (2026-08-08)
    const bookingRules = await publicBookingRulesService.getRules();
    // EFFECTIVE duration = base bumped to the new-client minimum. The guard AND the stored appointment
    // MUST both use THIS value (2026-08-09 fix — see the note above reqStart).
    const effectiveDuration = durationForBooking(appointmentData.duration, isNew, bookingRules.newClientMinDuration);

    // 🚫 DOUBLE-BOOK GUARD — fresh server read, EFFECTIVE duration, same occupancy test as the picker.
    // Guests get NO emergency override; staff/emergency overlap flows don't come through here.
    if (reqStart != null) {
      // PERF (Jose 2026-11-02): the two guard reads are independent — fire them together, one round-trip.
      const [existing, blkSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'slotOccupancy'), // PII-free occupancy for the double-book guard (Security 2026-09-28)
          where('barberId', '==', appointmentData.barberId),
          where('date', '==', appointmentData.date),
        )),
        getDocs(query(
          collection(db, 'blockedTimes'),
          where('barberId', '==', appointmentData.barberId),
          where('date', '==', appointmentData.date),
        )).catch((e) => { console.warn('blockedTimes read failed (non-fatal):', e); return null; }),
      ]);
      const appts = existing.docs.map(d => ({
        barberId: d.data().barberId,
        time: d.data().time,
        duration: d.data().duration,
        status: d.data().status,
      }));
      // ⛔ Merge barber time-blocks so a guest booking can't land on a lunch/errand block.
      if (blkSnap) {
        blkSnap.docs.forEach(d => {
          const b: any = d.data();
          appts.push({ barberId: b.barberId, time: b.startTime, duration: b.duration || 30, status: 'confirmed' });
        });
      }
      const conflict = hasApptConflict(appointmentData.barberId, reqStart, effectiveDuration, appts);
      console.log(`%c[SLIK conflict-guard]%c public fresh re-check @ ${appointmentData.time} (${effectiveDuration}m${isNew ? ', new-client' : ''}) — ${appts.length} live appt(s) — ${conflict.conflict ? '🚫 BLOCKED (overlaps ' + conflict.withTime + ')' : '✅ clear'}`,
        'background:#7c3aed;color:#fff;padding:2px 6px;border-radius:4px;font-weight:bold', 'color:inherit');
      if (conflict.conflict) {
        const svc = (appointmentData as any).service || (appointmentData as any).serviceName || 'your service';
        const err: any = new Error(
          `There isn't enough time for ${svc} at ${appointmentData.time}` +
          (conflict.withTime ? ` — the next appointment is at ${conflict.withTime}` : '') +
          `. Please pick an earlier time or a slot with a longer opening.`
        );
        err.code = 'NOT_ENOUGH_TIME';
        throw err;
      }
    }

    // GUEST → REAL PROFILE (punch #3) — only now the guard has passed, so a blocked slot never orphans
    // a client. Deduped earlier; a genuine first-timer gets a real record so the no-show block + history
    // have a home instead of a guest_<ts> orphan.
    if (createGuestProfile) {
      try {
        const parts = (appointmentData.clientName || '').trim().split(/\s+/);
        const cRef = await addDoc(collection(db, 'clients'), {
          firstName: parts[0] || 'Client',
          lastName: parts.slice(1).join(' ') || '',
          phone: appointmentData.clientPhone || '',
          email: appointmentData.clientEmail || '',
          membershipType: 'regular',
          totalVisits: 0, totalSpent: 0, status: 'active', source: 'online',
          createdAt: Timestamp.now(),
        });
        resolvedClientId = cRef.id;
      } catch (ce) { console.warn('Guest profile create failed (non-fatal):', ce); }
    }

    const docRef = await addDoc(collection(db, 'appointments'), {
      ...appointmentData,
      ...(resolvedClientId ? { clientId: resolvedClientId } : {}),
      duration: effectiveDuration,
      isNewClient: isNew,
      ...((appointmentData as any).prepaidAgreement
        ? { prepaidAgreement: (appointmentData as any).prepaidAgreement, requiresPrepayment: true }
        : {}),
      // REAL upfront charge (Jose 2026-10-25): record the PaymentIntent + mark paid so the staff calendar
      // and reschedule gate see this appointment was prepaid in full.
      ...((appointmentData as any).prepaidPaymentIntentId
        ? { prepaidPaymentIntentId: (appointmentData as any).prepaidPaymentIntentId, prepaid: true, paymentStatus: 'paid', requiresPrepayment: true }
        : {}),
      createdAt: Timestamp.now(),
      status: 'scheduled',
      source: 'online',
      // 🏷️ SHOP SCOPE (Jose 2026-10-19): the staff calendar query filters appointments by shopId — an
      // appt WITHOUT it is invisible to staff (Firestore equality filters drop missing fields). Fresha
      // imports carry it; online bookings must too. Single-shop id for now (dynamic when multi-tenant/Nexsy).
      shopId: 'yjlC6wFnykikhlL6erGY',
      reminderSent: false
    });

    console.log('✅ Appointment created:', docRef.id);

    return {
      id: docRef.id,
      ...appointmentData,
      duration: effectiveDuration,
      createdAt: new Date()
    };
  }

  /**
   * Get customer appointments
   */
  async getCustomerAppointments(clientId: string): Promise<Appointment[]> {
    const q = query(
      collection(db, 'appointments'),
      where('clientId', '==', clientId)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date()
    })) as Appointment[];
  }

  /**
   * Get appointments booked by a parent for family members
   * These appointments have clientId = family member, but notes contain "Booked by parent (parentId)"
   */
  async getFamilyBookedAppointments(parentId: string): Promise<Appointment[]> {
    // Scoped to THIS parent's bookings via the `bookedBy` field (Security 2026-09-28) — was reading the
    // ENTIRE appointments collection + filtering by a notes string, which both leaked the whole book and
    // breaks once appointments is locked. bookedBy is set on every parent-for-family booking + is what the
    // owning-customer read rule allows. (Kept the legacy notes match as a belt-and-suspenders filter.)
    const snapshot = await getDocs(query(
      collection(db, 'appointments'),
      where('bookedBy', '==', parentId),
    ));

    const familyAppointments = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }) as Appointment);

    console.log(`🔍 Found ${familyAppointments.length} family-booked appointments for parent ${parentId}`);
    return familyAppointments;
  }

  /**
   * Subscribe to customer appointments (real-time)
   */
  subscribeToCustomerAppointments(
    clientId: string,
    callback: (appointments: Appointment[]) => void
  ): () => void {
    const q = query(
      collection(db, 'appointments'),
      where('clientId', '==', clientId)
    );

    return onSnapshot(q, (snapshot) => {
      const appointments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      })) as Appointment[];

      callback(appointments);
    });
  }
}

export const bookingService = new BookingService();
